import { useState, useEffect, type ReactNode } from 'react'
import { Box, Flex, VStack, Grid, Input, Spinner } from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useForm,
  Controller,
  useFormColors,
  FormLabel,
  WizardSidebar,
  WizardActions,
} from '@minetrace/ui/forms'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  LuBuilding2 as Building2,
  LuUsers as Users,
  LuTruck as TruckIcon,
  LuClipboardList as ClipboardList,
  LuSearch as Search,
  LuPlus as Plus,
  LuTrash2 as Trash2,
  LuPencil as Pencil,
  LuSend as Send,
  LuTriangleAlert as AlertTriangle,
  LuUser as User,
  LuFingerprint as Fingerprint,
  LuCreditCard as CreditCard,
  LuCalendar as Calendar,
  LuWeight as Weight,
  LuCar as Car,
  LuKeyRound as KeyRound,
} from 'react-icons/lu'
import { toaster, Text, useT } from '@minetrace/ui'
import { transportistasService, scrapingService } from '../../api/services'
import { useRequest } from '../../hooks'
import type { CreateTruckRequest, CreateDriverRequest } from '../../api/models/request/transportista'
import type { CarrierQuestionAnswerResponse, CarrierObservationResponse } from '../../api/models/response/transportista'
import UbigeoSelect from '../../components/UbigeoSelect'
import FormDatePicker from '../../components/FormDatePicker'
import { CompanyFieldLengths } from '../../lib/fieldLengths'
import { phoneRegister } from '../../lib/inputSanitizers'
import TruckModal from './TruckModal'
import DriverModal, { type DriverLocal } from './DriverModal'
import ObservationReplies from '../../components/ObservationReplies'
import SustainabilitySurvey from '../../components/SustainabilitySurvey'

type TabType = 'datos' | 'conductores' | 'flota' | 'encuesta'
const TAB_ORDER: TabType[] = ['datos', 'conductores', 'flota', 'encuesta']
// Mapeo tab → paso de aprobación (CarrierApprovalStep).
const TAB_STEP: Record<TabType, number> = { datos: 1, conductores: 2, flota: 3, encuesta: 4 }

const step1Schema = z.object({
  ruc: z.string().length(11, 'transportistas.nuevo.zod.rucLength'),
  legalName: z.string().min(1, 'transportistas.nuevo.zod.legalNameRequired'),
  commercialName: z.string().optional(),
  taxAddress: z.string().min(1, 'transportistas.nuevo.zod.taxAddressRequired'),
  ubigeoCode: z.string().length(6, 'transportistas.nuevo.zod.ubigeoRequired'),
  mainPhone: z.string().optional(),
  mainEmail: z.string().email('transportistas.nuevo.zod.emailInvalid').optional().or(z.literal('')),
  website: z.string().optional(),
})

type FormData = z.infer<typeof step1Schema>

const todayStr = () => new Date().toISOString().split('T')[0]!
const hasToken = (value: string | null | undefined, token: string) => (value ?? '').toUpperCase().includes(token)

// ── Tarjetas de lista (conductor / vehículo) ────────────────────────────────
// Misma estructura para ambos: avatar de identidad + título + chips etiquetados
// por campo + acciones. Mantiene jerarquía clara y consistencia visual.

function fmtDate(iso?: string): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('T')[0]!.split('-')
  return y && m && d ? `${d}/${m}/${y}` : iso
}

function isExpired(iso?: string): boolean {
  if (!iso) return false
  const d = new Date(iso.split('T')[0]! + 'T23:59:59')
  return !isNaN(d.getTime()) && d.getTime() < Date.now()
}

function InfoChip({ icon, label, value, tone = 'default' }: {
  icon: ReactNode; label: string; value: string; tone?: 'default' | 'danger'
}) {
  const c = useFormColors()
  const danger = tone === 'danger'
  return (
    <Flex align="center" gap={1.5} bg={danger ? `${c.error}10` : c.bgHover} borderRadius="md" px={2} py={1}>
      <Box display="flex" color={danger ? c.error : c.textMuted}>{icon}</Box>
      <Text fontSize="10px" fontWeight="600" color={danger ? c.error : c.textMuted} textTransform="uppercase" letterSpacing="0.04em">{label}</Text>
      <Text fontSize="xs" fontWeight="600" color={danger ? c.error : c.text}>{value}</Text>
    </Flex>
  )
}

function RowIconButton({ onClick, label, danger }: { onClick: () => void; label: string; danger?: boolean }) {
  const c = useFormColors()
  return (
    <Box
      as="button" type="button" aria-label={label} title={label}
      w="36px" h="36px" display="flex" alignItems="center" justifyContent="center"
      borderRadius="lg" cursor="pointer" transition="background 0.15s, color 0.15s"
      color={danger ? c.error : c.textMuted}
      _hover={{ bg: danger ? `${c.error}18` : c.bgInput, color: danger ? c.error : c.text }}
      onClick={onClick}
    >
      {danger ? <Trash2 size={16} /> : <Pencil size={16} />}
    </Box>
  )
}

function EntityCard({ icon, title, badge, chips, onEdit, onRemove, editLabel, removeLabel }: {
  icon: ReactNode; title: string; badge?: ReactNode; chips: ReactNode
  onEdit: () => void; onRemove: () => void; editLabel: string; removeLabel: string
}) {
  const c = useFormColors()
  return (
    <Flex
      p={4} bg="white" border="1px solid" borderColor={c.border} borderRadius="xl"
      alignItems="center" gap={3} transition="border-color 0.15s, box-shadow 0.15s"
      _hover={{ borderColor: c.accent, boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 6px 16px rgba(16,24,40,0.06)' }}
    >
      <Flex w="42px" h="42px" flexShrink={0} borderRadius="full" alignItems="center" justifyContent="center" bg={`${c.accent}14`} color={c.accent}>
        {icon}
      </Flex>
      <Box flex={1} minW={0}>
        <Flex alignItems="center" gap={2} mb={2}>
          <Text fontSize="sm" fontWeight="700" color={c.text} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">{title}</Text>
          {badge}
        </Flex>
        <Flex gap={2} rowGap={2} flexWrap="wrap">{chips}</Flex>
      </Box>
      <Flex gap={1} flexShrink={0}>
        <RowIconButton onClick={onEdit} label={editLabel} />
        <RowIconButton onClick={onRemove} label={removeLabel} danger />
      </Flex>
    </Flex>
  )
}

function AccountBadge({ label }: { label: string }) {
  return (
    <Flex flexShrink={0} alignItems="center" gap={1} px={2} py={0.5} borderRadius="full" bg="rgba(42, 138, 135,0.12)">
      <KeyRound size={11} color="#1d6664" />
      <Text fontSize="10px" fontWeight="700" color="#1d6664">{label}</Text>
    </Flex>
  )
}

export default function NuevoTransportistaPage() {
  const navigate = useNavigate()
  const c = useFormColors()
  const { t } = useT()
  const { id } = useParams()
  const isEditing = Boolean(id)
  const backPath = '/app/transportistas'

  const [activeTab, setActiveTab] = useState<TabType>('datos')
  const [companyId, setCompanyId] = useState<string | null>(id ?? null)
  const [loadingCompany, setLoadingCompany] = useState(isEditing)
  const [loadedStatus, setLoadedStatus] = useState(0)
  const [registrationStatus, setRegistrationStatus] = useState(0)
  const [wizardCompleted, setWizardCompleted] = useState(false)
  const [completedSteps, setCompletedSteps] = useState<Set<TabType>>(new Set())
  const [observations, setObservations] = useState<CarrierObservationResponse[]>([])

  const [sunatConsulted, setSunatConsulted] = useState(false)
  const [sunat, setSunat] = useState({ estado: '', condicion: '', tipo: '' })
  const [economicActivities, setEconomicActivities] = useState<string[]>([])
  const [registrationDate, setRegistrationDate] = useState<string>(todayStr())

  const [trucks, setTrucks] = useState<CreateTruckRequest[]>([])
  const [drivers, setDrivers] = useState<DriverLocal[]>([])

  // Encuesta de sostenibilidad (4º paso)
  const [questions, setQuestions] = useState<CarrierQuestionAnswerResponse[]>([])
  const [questionsLoaded, setQuestionsLoaded] = useState(false)
  const [loadingQuestions, setLoadingQuestions] = useState(false)

  const [truckModalOpen, setTruckModalOpen] = useState(false)
  const [editingTruckIndex, setEditingTruckIndex] = useState<number | null>(null)
  const [driverModalOpen, setDriverModalOpen] = useState(false)
  const [editingDriverIndex, setEditingDriverIndex] = useState<number | null>(null)
  const [truckModalSeq, setTruckModalSeq] = useState(0)
  const [driverModalSeq, setDriverModalSeq] = useState(0)

  const { execute: consultarSunat, isFetching: isSearchingSunat } = useRequest(scrapingService.consultarSunat)
  const { execute: execCreateStep1 } = useRequest(transportistasService.createStep1)
  const { execute: execUpdateStep3 } = useRequest(
    (params: { id: string; trucks: CreateTruckRequest[] }) => transportistasService.updateStep3(params.id, params.trucks),
  )
  const { execute: execUpdateStep4 } = useRequest(
    (params: { id: string; drivers: CreateDriverRequest[] }) => transportistasService.updateStep4(params.id, params.drivers),
  )
  const { execute: execSaveSurvey } = useRequest(
    (params: { id: string; answers: { questionId: number; answer: boolean | null; comments?: string }[] }) =>
      transportistasService.saveSustainability(params.id, { answers: params.answers }).then(() => true as const),
  )
  const { execute: execFinalize } = useRequest((cid: string) => transportistasService.finalize(cid).then(() => true as const))
  const { execute: execSubmitReview } = useRequest((cid: string) => transportistasService.submitForReview(cid).then(() => true as const))

  const formMethods = useForm<FormData>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      ruc: '', legalName: '', commercialName: '',
      taxAddress: '', ubigeoCode: '', mainPhone: '', mainEmail: '', website: '',
    },
  })
  const { register, watch, setValue, control, reset, trigger, getValues, handleSubmit, formState: { errors, isSubmitting } } = formMethods

  useEffect(() => {
    if (!id) return
    const load = async () => {
      setLoadingCompany(true)
      try {
        const data = await transportistasService.getById(id)
        setLoadedStatus(data.status)
        setRegistrationStatus(data.registrationStatus)
        setWizardCompleted(Boolean(data.wizardCompletedAt))
        setObservations(data.observations ?? [])
        reset({
          ruc: data.ruc,
          legalName: data.legalName,
          commercialName: data.commercialName ?? '',
          taxAddress: data.taxAddress ?? '',
          ubigeoCode: data.ubigeoCode ?? '',
          mainPhone: data.mainPhone ?? '',
          mainEmail: data.mainEmail ?? '',
          website: data.website ?? '',
        })
        setCompanyId(data.id)
        setRegistrationDate(data.registrationDate ?? todayStr())
        setEconomicActivities(data.economicActivities ?? [])
        setSunat({ estado: data.taxpayerStatus ?? '', condicion: data.taxpayerCondition ?? '', tipo: data.taxpayerType ?? '' })
        if (data.taxpayerStatus || data.taxpayerCondition) setSunatConsulted(true)
        if (data.trucks) {
          setTrucks(data.trucks.map((tr) => ({
            placa: tr.placa, marca: tr.marca ?? '', modelo: tr.modelo ?? '',
            anio: tr.anio ?? undefined, capacidadToneladas: tr.capacidadToneladas ?? undefined,
            numeroConstanciaInscripcion: tr.numeroConstanciaInscripcion ?? '',
            vehicleTypeId: tr.vehicleTypeId ?? undefined, fuelTypeId: tr.fuelTypeId ?? undefined,
            performanceKmPerGal: tr.performanceKmPerGal ?? undefined,
            gpsProvider: tr.gpsProvider ?? undefined, gpsType: tr.gpsType ?? undefined,
            gpsUrl: tr.gpsUrl ?? undefined, gpsIsActive: tr.gpsIsActive ?? undefined,
          })))
        }
        if (data.drivers) {
          setDrivers(data.drivers.map((dr) => ({
            personId: dr.personId, fullName: dr.fullName, identityDoc: dr.identityDoc ?? '',
            licenseNumber: dr.licenseNumber ?? '', licenseCategory: dr.licenseCategory ?? '',
            licenseExpiryDate: dr.licenseExpiryDate ? dr.licenseExpiryDate.split('T')[0]! : '',
            hasAccount: dr.hasAccount, username: dr.username ?? undefined,
          })))
        }
      } catch {
        // silencioso
      } finally {
        setLoadingCompany(false)
      }
    }
    load()
  }, [id, reset])

  // Carga perezosa del cuestionario al entrar al paso Encuesta (requiere companyId).
  useEffect(() => {
    const cid = companyId ?? id
    if (activeTab !== 'encuesta' || !cid || questionsLoaded) return
    setLoadingQuestions(true)
    transportistasService
      .getCompliance(cid)
      .then((d) => { setQuestions(d.questions); setQuestionsLoaded(true) })
      .catch(() => {})
      .finally(() => setLoadingQuestions(false))
  }, [activeTab, companyId, id, questionsLoaded])

  const handleConsultarSunat = async () => {
    const ruc = watch('ruc')
    if (!ruc || ruc.length !== 11) return
    const data = await consultarSunat(ruc)
    if (!data) return
    if (data.razonSocial) setValue('legalName', data.razonSocial)
    if (data.nombreComercial) setValue('commercialName', data.nombreComercial)
    if (data.direccion) setValue('taxAddress', data.direccion)
    if (data.actividadesEconomicas?.length) setEconomicActivities(data.actividadesEconomicas)
    setSunat({ estado: data.estadoContribuyente ?? '', condicion: data.condicionContribuyente ?? '', tipo: data.tipoContribuyente ?? '' })
    setSunatConsulted(true)
  }

  const buildStep1 = (data: FormData, cid?: string) => ({
    id: cid ?? companyId ?? id ?? undefined,
    ruc: data.ruc,
    legalName: data.legalName,
    commercialName: data.commercialName || undefined,
    economicActivities: economicActivities.length > 0 ? economicActivities : undefined,
    taxAddress: data.taxAddress || undefined,
    ubigeoCode: data.ubigeoCode || undefined,
    mainPhone: data.mainPhone || undefined,
    mainEmail: data.mainEmail || undefined,
    website: data.website || undefined,
    sunatVerified: sunatConsulted || undefined,
    registrationDate: registrationDate || undefined,
    taxpayerStatus: sunat.estado || undefined,
    taxpayerCondition: sunat.condicion || undefined,
    taxpayerType: sunat.tipo || undefined,
  })

  const driversPayload = (): CreateDriverRequest[] =>
    drivers.map((d) => ({
      personId: d.personId,
      licenseNumber: d.licenseNumber,
      licenseCategory: d.licenseCategory,
      licenseExpiryDate: d.licenseExpiryDate,
      createAccount: d.createAccount,
      username: d.username,
      password: d.password,
    }))

  const surveyPayload = () => questions.map((q) => ({ questionId: q.questionId, answer: q.answer, comments: q.comments ?? undefined }))

  // ── Guardado por etapa (modo alta) ──
  const handleWizardStep = async (data: FormData) => {
    if (activeTab === 'datos') {
      const result = await execCreateStep1(buildStep1(data))
      if (!result) return
      setCompanyId(result.id)
      setCompletedSteps((prev) => new Set(prev).add('datos'))
      setActiveTab('conductores')
    } else if (activeTab === 'conductores') {
      const cid = companyId ?? id
      if (!cid) { toaster.error({ description: t('transportistas.nuevo.errors.noTransportistaInProcess') }); return }
      const r = await execUpdateStep4({ id: cid, drivers: driversPayload() })
      if (!r) return
      setCompletedSteps((prev) => new Set(prev).add('conductores'))
      setActiveTab('flota')
    } else if (activeTab === 'flota') {
      const cid = companyId ?? id
      if (!cid) { toaster.error({ description: t('transportistas.nuevo.errors.noTransportistaInProcess') }); return }
      const r = await execUpdateStep3({ id: cid, trucks })
      if (!r) return
      setCompletedSteps((prev) => new Set(prev).add('flota'))
      setActiveTab('encuesta')
    } else {
      const cid = companyId ?? id
      if (!cid) { toaster.error({ description: t('transportistas.nuevo.errors.noTransportistaInProcess') }); return }
      const ok = await execSaveSurvey({ id: cid, answers: surveyPayload() })
      if (!ok) return
      // Finaliza el wizard: queda "Registrado". El envío a evaluación es posterior.
      const fin = await execFinalize(cid)
      if (!fin) return
      toaster.success({ description: t('transportistas.nuevo.toasts.created') })
      setTimeout(() => navigate(backPath), 800)
    }
  }

  // ── Guardado por etapa (modo edición): persiste solo la etapa del tab activo ──
  const saveCurrentStage = async (): Promise<boolean> => {
    const cid = companyId ?? id
    if (activeTab === 'datos') {
      const valid = await trigger(['ruc', 'legalName', 'mainEmail', 'taxAddress', 'ubigeoCode'])
      if (!valid) return false
      const result = await execCreateStep1(buildStep1(getValues()))
      if (!result) return false
      setCompanyId(result.id)
      return true
    }
    if (!cid) { toaster.error({ description: t('transportistas.nuevo.errors.noTransportistaInProcess') }); return false }
    if (activeTab === 'conductores') { const r = await execUpdateStep4({ id: cid, drivers: driversPayload() }); return Boolean(r) }
    if (activeTab === 'flota') { const r = await execUpdateStep3({ id: cid, trucks }); return Boolean(r) }
    const rs = await execSaveSurvey({ id: cid, answers: surveyPayload() })
    return Boolean(rs)
  }

  const handleSaveContinue = async () => {
    if (!(await saveCurrentStage())) return
    const idx = TAB_ORDER.indexOf(activeTab)
    if (idx < TAB_ORDER.length - 1) {
      toaster.success({ description: t('transportistas.nuevo.toasts.stageSaved') })
      setActiveTab(TAB_ORDER[idx + 1]!)
    } else {
      toaster.success({ description: t('transportistas.nuevo.toasts.updated') })
      setTimeout(() => navigate(backPath), 800)
    }
  }
  const handleSaveExitStage = async () => {
    if (!(await saveCurrentStage())) return
    toaster.success({ description: t('transportistas.nuevo.toasts.updated') })
    setTimeout(() => navigate(backPath), 800)
  }

  // Guarda las 4 etapas (para no perder ediciones) y recién envía a evaluación.
  const submitReviewFlow = async (data: FormData) => {
    const result = await execCreateStep1(buildStep1(data))
    if (!result) return
    setCompanyId(result.id)
    const r4 = await execUpdateStep4({ id: result.id, drivers: driversPayload() })
    if (!r4) return
    const r3 = await execUpdateStep3({ id: result.id, trucks })
    if (!r3) return
    if (questionsLoaded) {
      const rs = await execSaveSurvey({ id: result.id, answers: surveyPayload() })
      if (!rs) return
    }
    const ok = await execSubmitReview(result.id)
    if (!ok) return
    toaster.success({ description: t('transportistas.nuevo.toasts.submitted') })
    setTimeout(() => navigate(backPath), 800)
  }
  const handleSubmitToReview = () => { handleSubmit(submitReviewFlow)() }

  const STEP_FIELDS: Record<TabType, (keyof FormData)[]> = {
    datos: ['ruc', 'legalName', 'mainEmail', 'taxAddress', 'ubigeoCode'],
    conductores: [],
    flota: [],
    encuesta: [],
  }

  const handleStepSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const fields = STEP_FIELDS[activeTab]
    const valid = fields.length === 0 || (await trigger(fields))
    if (valid) await handleWizardStep(getValues())
  }


  const handleTabChange = (tab: TabType) => {
    if (isEditing) { setActiveTab(tab); return }
    const idx = TAB_ORDER.indexOf(tab)
    const curIdx = TAB_ORDER.indexOf(activeTab)
    if (idx <= curIdx || completedSteps.has(TAB_ORDER[idx - 1]!)) setActiveTab(tab)
  }
  const handleBack = () => {
    const idx = TAB_ORDER.indexOf(activeTab)
    if (idx > 0) setActiveTab(TAB_ORDER[idx - 1]!)
  }

  // ── Modales ──
  const openAddTruck = () => { setEditingTruckIndex(null); setTruckModalSeq((s) => s + 1); setTruckModalOpen(true) }
  const openEditTruck = (i: number) => { setEditingTruckIndex(i); setTruckModalSeq((s) => s + 1); setTruckModalOpen(true) }
  const saveTruck = (truck: CreateTruckRequest) => setTrucks((prev) => {
    if (editingTruckIndex != null) return prev.map((x, i) => (i === editingTruckIndex ? truck : x))
    // Una placa no puede repetirse dentro del mismo transportista (normalizada).
    const placa = truck.placa.trim().toUpperCase()
    if (prev.some((tr) => tr.placa.trim().toUpperCase() === placa)) { toaster.error({ description: t('transportistas.nuevo.truckExtras.duplicate') }); return prev }
    return [...prev, truck]
  })
  const removeTruck = (i: number) => setTrucks((prev) => prev.filter((_, idx) => idx !== i))

  const openAddDriver = () => { setEditingDriverIndex(null); setDriverModalSeq((s) => s + 1); setDriverModalOpen(true) }
  const openEditDriver = (i: number) => { setEditingDriverIndex(i); setDriverModalSeq((s) => s + 1); setDriverModalOpen(true) }
  const saveDriver = (driver: DriverLocal) => setDrivers((prev) => {
    if (editingDriverIndex != null) return prev.map((x, i) => (i === editingDriverIndex ? driver : x))
    if (prev.some((d) => d.personId === driver.personId)) { toaster.error({ description: t('transportistas.nuevo.driverExtras.duplicate') }); return prev }
    return [...prev, driver]
  })
  const removeDriver = (i: number) => setDrivers((prev) => prev.filter((_, idx) => idx !== i))

  const setAnswer = (qid: number, answer: boolean | null) => setQuestions((prev) => prev.map((q) => (q.questionId === qid ? { ...q, answer } : q)))
  const setComment = (qid: number, comments: string) => setQuestions((prev) => prev.map((q) => (q.questionId === qid ? { ...q, comments } : q)))

  const inputStyles = {
    bg: c.bgInput, border: 'none', borderRadius: 'xl', color: c.text,
    _placeholder: { color: c.textMuted }, _focus: { outline: 'none', ring: '1px', ringColor: c.accent },
  } as const
  const errorInputStyles = { ...inputStyles, ring: '1px', ringColor: c.error } as const
  const addBtnStyles = {
    display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, borderRadius: 'lg',
    border: '1px solid', borderColor: c.border, color: c.text, fontSize: 'sm', fontWeight: '600',
    cursor: 'pointer', _hover: { bg: c.bgHover },
  } as const

  // Campo de solo lectura para Estado/Condición SUNAT (verde si OK, rojo si no, gris si vacío).
  const renderSunatStatusField = (value: string, ok: boolean) => (
    <Box px={3} py={2.5} borderRadius="xl" fontWeight="600" fontSize="sm"
      bg={value ? (ok ? 'rgba(42, 138, 135, 0.12)' : 'rgba(200, 68, 47, 0.12)') : c.bgInput}
      color={value ? (ok ? '#1d6664' : '#b23a26') : c.textMuted}>
      {value || '—'}
    </Box>
  )

  // Recarga las observaciones (con su hilo de respuestas) tras responder.
  const refreshObservations = async () => {
    const cid = companyId ?? id
    if (!cid) return
    try {
      const d = await transportistasService.getById(cid)
      setObservations(d.observations ?? [])
    } catch { /* ignore */ }
  }
  const handleObsReply = async (observationId: string, text: string) => {
    const cid = companyId ?? id
    if (!cid) return
    try {
      await transportistasService.addObservationReply(cid, observationId, text)
      await refreshObservations()
    } catch {
      toaster.error({ description: t('transportistas.observations.replyError') })
    }
  }

  // Observaciones abiertas del paso activo (modo Observado).
  const openObsForActiveTab = observations.filter((o) => o.step === TAB_STEP[activeTab] && !o.resolvedAt)
  const renderTabObservations = () => {
    if (openObsForActiveTab.length === 0) return null
    return (
      <Box mb={5} p={4} borderRadius="xl" bg="rgba(212, 154, 42, 0.08)" border="1px solid" borderColor="rgba(212, 154, 42, 0.35)">
        <Flex alignItems="center" gap={2} mb={3}>
          <AlertTriangle size={16} color="#8a5f0b" />
          <Text fontSize="sm" fontWeight="700" color="#8a5f0b">{t('transportistas.nuevo.observations.tabTitle')}</Text>
        </Flex>
        <VStack gap={4} alignItems="stretch">
          {openObsForActiveTab.map((o) => (
            <Box key={o.id}>
              <Text fontSize="sm" color={c.text} fontWeight="500">{o.observationText}</Text>
              <ObservationReplies replies={o.replies ?? []} canReply onReply={(text) => handleObsReply(o.id, text)} />
            </Box>
          ))}
        </VStack>
        <Text fontSize="xs" color={c.textMuted} mt={3}>{t('transportistas.nuevo.observations.tabHint')}</Text>
      </Box>
    )
  }

  const empresaHasErrors = Boolean(errors.ruc || errors.legalName || errors.mainEmail || errors.taxAddress || errors.ubigeoCode)
  const obsCountByTab = (tab: TabType) => observations.filter((o) => o.step === TAB_STEP[tab] && !o.resolvedAt).length
  const tabs = [
    { key: 'datos' as const, label: t('transportistas.nuevo.tabs.datos'), icon: <Building2 size={18} />, hasError: empresaHasErrors, observationCount: obsCountByTab('datos'), status: obsCountByTab('datos') > 0 ? ('observed' as const) : undefined },
    { key: 'conductores' as const, label: t('transportistas.nuevo.tabs.conductores', { count: drivers.length }), icon: <Users size={18} />, observationCount: obsCountByTab('conductores'), status: obsCountByTab('conductores') > 0 ? ('observed' as const) : undefined },
    { key: 'flota' as const, label: t('transportistas.nuevo.tabs.flota', { count: trucks.length }), icon: <TruckIcon size={18} />, observationCount: obsCountByTab('flota'), status: obsCountByTab('flota') > 0 ? ('observed' as const) : undefined },
    { key: 'encuesta' as const, label: t('transportistas.nuevo.tabs.encuesta'), icon: <ClipboardList size={18} />, observationCount: obsCountByTab('encuesta'), status: obsCountByTab('encuesta') > 0 ? ('observed' as const) : undefined },
  ]
  const STEP_TITLES: Record<TabType, { title: string; description: string }> = {
    datos: { title: t('transportistas.nuevo.sections.datosGenerales'), description: t('transportistas.nuevo.sections.datosDescription') },
    conductores: { title: t('transportistas.nuevo.sections.afiliacionConductores'), description: t('transportistas.nuevo.sections.conductoresDescription') },
    flota: { title: t('transportistas.nuevo.sections.registroFlota'), description: t('transportistas.nuevo.sections.vehiculosDescription') },
    encuesta: { title: t('transportistas.nuevo.sections.encuesta'), description: t('transportistas.nuevo.sections.encuestaDescription') },
  }
  const currentStep = STEP_TITLES[activeTab]
  const activeIndex = TAB_ORDER.indexOf(activeTab)
  // El botón "Enviar a evaluación" aparece cuando el registro está completo pero
  // aún no aprobado (Registrado=Draft+wizardCompleted, u Observado).
  const canSubmitToReview = wizardCompleted && (registrationStatus === 0 || registrationStatus === 2)

  if (loadingCompany) {
    return (
      <VStack gap={6} alignItems="stretch">
        <Text fontSize="2xl" fontWeight="800" color={c.text}>{t('transportistas.nuevo.loadingTitle')}</Text>
        <Box p={8} textAlign="center"><Text color={c.textMuted}>{t('transportistas.nuevo.loadingData')}</Text></Box>
      </VStack>
    )
  }

  return (
    <>
      <Flex mx={-8} mt={-4}>
        {/* Sidebar de pasos (mismo patrón que el registro de minero) */}
        <WizardSidebar
          title={isEditing ? t('transportistas.nuevo.titleEdit') : t('transportistas.nuevo.titleNew')}
          subtitle={t('transportistas.nuevo.stepCounter', { current: activeIndex + 1, total: TAB_ORDER.length })}
          steps={tabs}
          activeStep={activeTab}
          completedSteps={completedSteps}
          onStepClick={handleTabChange}
        />

        {/* Contenido del paso */}
        <Box flex={1} py={4} px={6} display="flex" flexDirection="column" minH="calc(100vh - 2rem)">
          {/* Encabezado del paso */}
          <Flex justifyContent="space-between" alignItems="flex-start" gap={4} mb={6}>
            <Box>
              {isEditing && watch('legalName') && (
                <Flex gap={3} alignItems="center" mb={2}>
                  <Text fontSize="sm" fontWeight="700" color={c.text}>{watch('legalName')}</Text>
                  {watch('ruc') && (
                    <Text fontSize="xs" color={c.textMuted} fontFamily="monospace" bg={c.bgHover} px={2} py={0.5} borderRadius="md">{watch('ruc')}</Text>
                  )}
                </Flex>
              )}
              <Text fontSize="3xl" fontWeight="800" color={c.accent}>{currentStep.title}</Text>
              <Text color={c.textMuted} mt={2} maxW="2xl">{currentStep.description}</Text>
            </Box>
            <Flex gap={2} flexShrink={0}>
              {canSubmitToReview && (
                <Box as="button" type="button" display="flex" alignItems="center" gap={2} px={4} py={2.5} borderRadius="lg" bg={c.accent} color="white" fontWeight="600" fontSize="sm" cursor="pointer" _hover={{ opacity: 0.9 }} onClick={handleSubmitToReview}>
                  <Send size={16} />
                  {t('transportistas.nuevo.submitToReview')}
                </Box>
              )}
            </Flex>
          </Flex>

          <FormProvider {...formMethods}>
            <form onSubmit={handleStepSubmit}>
              {renderTabObservations()}

              {/* Paso Datos Generales */}
              {activeTab === 'datos' && (
                <Box>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                    {/* RUC primero: de aquí parte la consulta SUNAT y el resto de los datos */}
                    <Box gridColumn={{ md: 'span 2' }}>
                      <FormLabel required>{t('transportistas.nuevo.fields.ruc')}</FormLabel>
                      <Flex gap={2}>
                        <Input {...register('ruc')} placeholder={t('transportistas.nuevo.fields.rucPlaceholder')} maxLength={CompanyFieldLengths.ruc} fontFamily="monospace" disabled={isEditing} {...(errors.ruc ? errorInputStyles : inputStyles)} {...(isEditing ? { cursor: 'not-allowed', opacity: 0.7 } : {})} />
                        <Box as="button" type="button" display="flex" alignItems="center" justifyContent="center" px={4} bg={c.accentGradient} color="white" borderRadius="xl"
                          cursor={watch('ruc')?.length !== 11 || isSearchingSunat ? 'not-allowed' : 'pointer'} opacity={watch('ruc')?.length !== 11 || isSearchingSunat ? 0.5 : 1} flexShrink={0} minW="48px" onClick={handleConsultarSunat}>
                          {isSearchingSunat ? <Spinner size="sm" color="white" /> : <Search size={18} />}
                        </Box>
                      </Flex>
                      <Text tid={isEditing ? 'transportistas.nuevo.fields.rucLocked' : 'transportistas.nuevo.fields.rucHelp'} fontSize="xs" color={c.textMuted} mt={1} />
                      {errors.ruc && <Text fontSize="xs" color={c.error} mt={1}>{t(errors.ruc.message ?? '')}</Text>}
                    </Box>
                    <Box>
                      <FormLabel required>{t('transportistas.nuevo.fields.legalName')}</FormLabel>
                      <Input {...register('legalName')} placeholder={t('transportistas.nuevo.fields.legalNamePlaceholder')} maxLength={CompanyFieldLengths.legalName} {...(errors.legalName ? errorInputStyles : inputStyles)} />
                      {errors.legalName && <Text fontSize="xs" color={c.error} mt={1}>{t(errors.legalName.message ?? '')}</Text>}
                    </Box>
                    <Box>
                      <FormLabel>{t('transportistas.nuevo.fields.commercialName')}</FormLabel>
                      <Input {...register('commercialName')} placeholder={t('transportistas.nuevo.fields.commercialNamePlaceholder')} maxLength={CompanyFieldLengths.commercialName} {...inputStyles} />
                    </Box>
                    <Box>
                      <FormLabel>{t('transportistas.nuevo.fields.taxpayerStatus')}</FormLabel>
                      {renderSunatStatusField(sunat.estado, hasToken(sunat.estado, 'ACTIVO'))}
                    </Box>
                    <Box>
                      <FormLabel>{t('transportistas.nuevo.fields.taxpayerCondition')}</FormLabel>
                      {renderSunatStatusField(sunat.condicion, hasToken(sunat.condicion, 'HABIDO') && !hasToken(sunat.condicion, 'NO HABIDO'))}
                    </Box>
                    <Box gridColumn={{ md: 'span 2' }}>
                      <FormLabel>{t('transportistas.nuevo.fields.economicActivities')}</FormLabel>
                      {economicActivities.length > 0 ? (
                        <VStack gap={1} alignItems="stretch">
                          {economicActivities.map((a, i) => (
                            <Box key={i} p={2.5} bg={c.bgHover} borderRadius="lg"><Text fontSize="sm" color={c.text}>{a}</Text></Box>
                          ))}
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color={c.textMuted}>{t('transportistas.nuevo.fields.economicActivitiesEmpty')}</Text>
                      )}
                    </Box>
                    <Box gridColumn={{ md: 'span 2' }}>
                      <Controller name="ubigeoCode" control={control} render={({ field }) => <UbigeoSelect value={field.value ?? ''} onChange={field.onChange} required />} />
                      {errors.ubigeoCode && <Text fontSize="xs" color={c.error} mt={1}>{t(errors.ubigeoCode.message ?? '')}</Text>}
                    </Box>
                    <Box gridColumn={{ md: 'span 2' }}>
                      <FormLabel required>{t('transportistas.nuevo.fields.taxAddress')}</FormLabel>
                      <Input {...register('taxAddress')} placeholder={t('transportistas.nuevo.fields.taxAddressPlaceholder')} maxLength={CompanyFieldLengths.taxAddress} {...(errors.taxAddress ? errorInputStyles : inputStyles)} />
                      {errors.taxAddress && <Text fontSize="xs" color={c.error} mt={1}>{t(errors.taxAddress.message ?? '')}</Text>}
                    </Box>
                    <Box>
                      <FormLabel>{t('transportistas.nuevo.fields.email')}</FormLabel>
                      <Input type="email" {...register('mainEmail')} placeholder={t('transportistas.nuevo.fields.emailPlaceholder')} maxLength={CompanyFieldLengths.mainEmail} {...(errors.mainEmail ? errorInputStyles : inputStyles)} />
                      {errors.mainEmail && <Text fontSize="xs" color={c.error} mt={1}>{t(errors.mainEmail.message ?? '')}</Text>}
                    </Box>
                    <Box>
                      <FormLabel>{t('transportistas.nuevo.fields.phone')}</FormLabel>
                      <Input {...phoneRegister(register('mainPhone'))} placeholder={t('transportistas.nuevo.fields.phonePlaceholder')} maxLength={CompanyFieldLengths.mainPhone} {...inputStyles} />
                    </Box>
                    <Box>
                      <FormLabel required>{t('transportistas.nuevo.fields.registrationDate')}</FormLabel>
                      <FormDatePicker value={registrationDate} onChange={setRegistrationDate} maxDate={todayStr()} placeholder={t('transportistas.nuevo.fields.registrationDatePlaceholder')} />
                      <Text tid="transportistas.nuevo.fields.registrationDateHelp" fontSize="xs" color={c.textMuted} mt={1} />
                    </Box>
                  </Grid>
                  {sunatConsulted && (!hasToken(sunat.estado, 'ACTIVO') || hasToken(sunat.condicion, 'NO HABIDO')) && (
                    <Text tid="transportistas.nuevo.fields.sunatWarning" fontSize="xs" color="#b23a26" mt={3} />
                  )}
                </Box>
              )}

              {/* Paso Conductores */}
              {activeTab === 'conductores' && (
                <Box>
                  <Flex justifyContent="flex-end" mb={3}>
                    <Box as="button" type="button" onClick={openAddDriver} {...addBtnStyles}>
                      <Plus size={16} />{t('transportistas.nuevo.driverExtras.addButton')}
                    </Box>
                  </Flex>
                  {drivers.length === 0 ? (
                    <Box p={6} textAlign="center" bg={c.bgHover} borderRadius="xl"><Text tid="transportistas.nuevo.driverExtras.emptyList" fontSize="sm" color={c.textMuted} /></Box>
                  ) : (
                    <VStack gap={3} alignItems="stretch">
                      {drivers.map((driver, index) => (
                        <EntityCard
                          key={`${driver.personId}-${index}`}
                          icon={<User size={20} />}
                          title={driver.fullName}
                          badge={driver.hasAccount ? <AccountBadge label={t('transportistas.nuevo.driverExtras.hasAccountBadge')} /> : undefined}
                          chips={
                            <>
                              {driver.identityDoc && <InfoChip icon={<Fingerprint size={13} />} label="DNI" value={driver.identityDoc} />}
                              {driver.licenseNumber && (
                                <InfoChip
                                  icon={<CreditCard size={13} />}
                                  label="Licencia"
                                  value={`${driver.licenseNumber}${driver.licenseCategory ? ` · ${driver.licenseCategory}` : ''}`}
                                />
                              )}
                              {driver.licenseExpiryDate && (
                                <InfoChip
                                  icon={<Calendar size={13} />}
                                  label={isExpired(driver.licenseExpiryDate) ? 'Vencida' : 'Vence'}
                                  value={fmtDate(driver.licenseExpiryDate)}
                                  tone={isExpired(driver.licenseExpiryDate) ? 'danger' : 'default'}
                                />
                              )}
                            </>
                          }
                          onEdit={() => openEditDriver(index)}
                          onRemove={() => removeDriver(index)}
                          editLabel={`Editar conductor ${driver.fullName}`}
                          removeLabel={`Eliminar conductor ${driver.fullName}`}
                        />
                      ))}
                    </VStack>
                  )}
                </Box>
              )}

              {/* Paso Flota */}
              {activeTab === 'flota' && (
                <Box>
                  <Flex justifyContent="flex-end" mb={3}>
                    <Box as="button" type="button" onClick={openAddTruck} {...addBtnStyles}>
                      <Plus size={16} />{t('transportistas.nuevo.truckExtras.addButton')}
                    </Box>
                  </Flex>
                  {trucks.length === 0 ? (
                    <Box p={6} textAlign="center" bg={c.bgHover} borderRadius="xl"><Text tid="transportistas.nuevo.truckExtras.emptyList" fontSize="sm" color={c.textMuted} /></Box>
                  ) : (
                    <VStack gap={3} alignItems="stretch">
                      {trucks.map((truck, index) => {
                        const vehicle = [truck.marca, truck.modelo, truck.anio ? `(${truck.anio})` : ''].filter(Boolean).join(' ')
                        return (
                          <EntityCard
                            key={`${truck.placa}-${index}`}
                            icon={<TruckIcon size={20} />}
                            title={truck.placa}
                            chips={
                              <>
                                {vehicle && <InfoChip icon={<Car size={13} />} label="Vehículo" value={vehicle} />}
                                {truck.capacidadToneladas != null && (
                                  <InfoChip icon={<Weight size={13} />} label="Capacidad" value={`${truck.capacidadToneladas} TM`} />
                                )}
                              </>
                            }
                            onEdit={() => openEditTruck(index)}
                            onRemove={() => removeTruck(index)}
                            editLabel={`Editar vehículo ${truck.placa}`}
                            removeLabel={`Eliminar vehículo ${truck.placa}`}
                          />
                        )
                      })}
                    </VStack>
                  )}
                </Box>
              )}

              {/* Paso Encuesta */}
              {activeTab === 'encuesta' && (
                <Box>
                  {loadingQuestions ? (
                    <Box p={6} textAlign="center"><Spinner size="sm" /></Box>
                  ) : (
                    <SustainabilitySurvey questions={questions} onAnswer={setAnswer} onComment={setComment} />
                  )}
                </Box>
              )}

              {isEditing && loadedStatus !== 0 ? (
                /* Edición: guardado por etapa con 3 opciones (no obliga a salir entre tabs) */
                <Flex alignItems="center" gap={3} mt={6} flexWrap="wrap">
                  <Box as="button" type="button" px={6} py={2.5} borderRadius="lg" color={c.textMuted} border="1px solid" borderColor={c.border} fontSize="sm" cursor="pointer" mr="auto" _hover={{ bg: c.bgHover }} onClick={() => navigate(backPath)}>
                    {t('common.cancel')}
                  </Box>
                  {activeTab !== 'datos' && (
                    <Box as="button" type="button" px={6} py={2.5} borderRadius="lg" color={c.textMuted} border="1px solid" borderColor={c.border} fontSize="sm" cursor="pointer" _hover={{ bg: c.bgHover }} onClick={handleBack}>
                      {t('transportistas.nuevo.actions.back')}
                    </Box>
                  )}
                  {activeTab !== 'encuesta' && (
                    <Box as="button" type="button" px={6} py={2.5} borderRadius="lg" color={c.accent} border="1px solid" borderColor={c.accent} fontWeight="600" fontSize="sm" cursor="pointer" _hover={{ bg: c.bgHover }} onClick={handleSaveContinue}>
                      {t('transportistas.nuevo.actions.saveContinue')}
                    </Box>
                  )}
                  <Box as="button" type="button" px={6} py={2.5} borderRadius="lg" bg={c.accent} color="white" fontWeight="bold" fontSize="sm" cursor="pointer" _hover={{ opacity: 0.9 }} onClick={handleSaveExitStage}>
                    {t('transportistas.nuevo.actions.saveExit')}
                  </Box>
                </Flex>
              ) : (
                <WizardActions
                  cancelPath={backPath}
                  isFirst={activeTab === 'datos'}
                  isLast={activeTab === 'encuesta'}
                  isEditing={false}
                  isSubmitting={isSubmitting}
                  onBack={handleBack}
                />
              )}
            </form>
          </FormProvider>
        </Box>
      </Flex>

      <TruckModal
        key={`truck-${truckModalSeq}`}
        open={truckModalOpen}
        onClose={() => setTruckModalOpen(false)}
        onSave={saveTruck}
        initial={editingTruckIndex != null ? trucks[editingTruckIndex] : null}
        existingPlacas={trucks.filter((_, i) => i !== editingTruckIndex).map((t) => t.placa.trim().toUpperCase())}
      />
      <DriverModal
        key={`driver-${driverModalSeq}`}
        open={driverModalOpen}
        onClose={() => setDriverModalOpen(false)}
        onSave={saveDriver}
        initial={editingDriverIndex != null ? drivers[editingDriverIndex] : null}
        existingPersonIds={drivers.filter((_, i) => i !== editingDriverIndex).map((d) => d.personId)}
      />
    </>
  )
}
